/**
 * Resolves upgrade/passive node effects for active skills via SkillProgressionComponent.
 *
 * Provides both the original hand-wired resolution methods (for existing skills)
 * and generic aggregation methods (for attunement tree skills). The generic API
 * supports additive, multiplicative, max, min, and boolean aggregation across
 * a skill's active upgrades, tree passives, or global passives.
 *
 * @see ADR-064 (shared systems architecture)
 */
import Phaser from 'phaser';
import { SkillProgressionComponent } from '../components/SkillProgressionComponent';
import { ConfigLoader } from './ConfigLoader';
import type { V4NodeDefinition } from '../utils/Types';

interface StaggerEffect {
  chance: number;
  durationMs: number;
}

interface ResolvedEffects {
  damageMultiplier: number;
  recoveryMsDelta: number;
  recoveryReduction: number;
  staggerChance: number;
  staggerDurationMs: number;
  movementSpeedMultiplier: number | undefined;
  proximityReduction: number | undefined;
}

// ---------------------------------------------------------------------------
// Effect key metadata — drives generic aggregation
// ---------------------------------------------------------------------------

export type AggregationType = 'additive' | 'multiplicative' | 'max' | 'min' | 'boolean';
export type EffectScope = 'skill' | 'tree' | 'global';

export interface EffectKeyMeta {
  key: string;
  aggregation: AggregationType;
  scope: EffectScope;
}

/**
 * Aggregates upgrade/passive node effects for active skills.
 *
 * Critical rule: upgrades apply to their `enhancesId` target only, NOT to evolutions.
 * If Punch evolves to Punchmore, upgrades targeting Punch (enhancesId: 'punch')
 * do NOT apply when the player is using Punchmore.
 */
export class UpgradeEffectResolver {
  private progression: SkillProgressionComponent;
  private cache: Map<string, ResolvedEffects> = new Map();
  private genericCache: Map<string, number | boolean | undefined> = new Map();
  private keyMeta: Map<string, EffectKeyMeta> = new Map();

  constructor(progression: SkillProgressionComponent, events: Phaser.Events.EventEmitter) {
    this.progression = progression;
    events.on('node-unlocked', () => { this.cache.clear(); this.genericCache.clear(); });
    events.on('tree-respec', () => { this.cache.clear(); this.genericCache.clear(); });
  }

  // -----------------------------------------------------------------------
  // Effect key metadata registry
  // -----------------------------------------------------------------------

  /** Register metadata for an effect key (aggregation type + scope). */
  registerKey(meta: EffectKeyMeta): void {
    this.keyMeta.set(meta.key, meta);
  }

  /** Bulk-register effect key metadata from a JSON record. */
  registerKeysFromJson(json: Record<string, { aggregation: AggregationType; scope: EffectScope }>): void {
    for (const [key, meta] of Object.entries(json)) {
      this.registerKey({ key, ...meta });
    }
  }

  /** Get registered metadata for a key (or undefined). */
  getKeyMeta(key: string): EffectKeyMeta | undefined {
    return this.keyMeta.get(key);
  }

  // -----------------------------------------------------------------------
  // Generic aggregation: skill-scoped (via getActiveUpgrades)
  // -----------------------------------------------------------------------

  /** Sum of a numeric effect key across a skill's active upgrades. Returns 0 if absent. */
  getAdditiveEffect(skillId: string, key: string): number {
    const cacheKey = `${skillId}:${key}:add`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number;

    let sum = 0;
    for (const node of this.progression.getActiveUpgrades(skillId)) {
      if (node.effect && typeof node.effect[key] === 'number') {
        sum += node.effect[key] as number;
      }
    }
    this.genericCache.set(cacheKey, sum);
    return sum;
  }

  /** Product of a numeric effect key across a skill's active upgrades. Base defaults to 1.0. */
  getMultiplicativeEffect(skillId: string, key: string, base = 1.0): number {
    const cacheKey = `${skillId}:${key}:mul`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number;

    let product = base;
    for (const node of this.progression.getActiveUpgrades(skillId)) {
      if (node.effect && typeof node.effect[key] === 'number') {
        product *= node.effect[key] as number;
      }
    }
    this.genericCache.set(cacheKey, product);
    return product;
  }

  /** Highest numeric value for a key across a skill's active upgrades. Returns undefined if absent. */
  getMaxEffect(skillId: string, key: string): number | undefined {
    const cacheKey = `${skillId}:${key}:max`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number | undefined;

    let max: number | undefined;
    for (const node of this.progression.getActiveUpgrades(skillId)) {
      if (node.effect && typeof node.effect[key] === 'number') {
        const val = node.effect[key] as number;
        max = max === undefined ? val : Math.max(max, val);
      }
    }
    this.genericCache.set(cacheKey, max);
    return max;
  }

  /** Lowest numeric value for a key across a skill's active upgrades. Returns undefined if absent. */
  getMinEffect(skillId: string, key: string): number | undefined {
    const cacheKey = `${skillId}:${key}:min`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number | undefined;

    let min: number | undefined;
    for (const node of this.progression.getActiveUpgrades(skillId)) {
      if (node.effect && typeof node.effect[key] === 'number') {
        const val = node.effect[key] as number;
        min = min === undefined ? val : Math.min(min, val);
      }
    }
    this.genericCache.set(cacheKey, min);
    return min;
  }

  /** True if any active upgrade for this skill carries a truthy value for the key. */
  hasEffect(skillId: string, key: string): boolean {
    const cacheKey = `${skillId}:${key}:has`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as boolean;

    for (const node of this.progression.getActiveUpgrades(skillId)) {
      if (node.effect && node.effect[key]) {
        this.genericCache.set(cacheKey, true);
        return true;
      }
    }
    this.genericCache.set(cacheKey, false);
    return false;
  }

  // -----------------------------------------------------------------------
  // Generic aggregation: tree-scoped passives
  // -----------------------------------------------------------------------

  /** Aggregate a key from unlocked passives within a single tree. */
  getTreePassiveEffect(
    treeName: string,
    key: string,
    aggregation: 'sum' | 'product' | 'max' | 'min',
  ): number {
    const cacheKey = `tree:${treeName}:${key}:${aggregation}`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number;

    let result = aggregation === 'product' ? 1.0 : (aggregation === 'min' ? Infinity : 0);
    let found = false;

    for (const passiveId of this.progression.getUnlockedPassives()) {
      const passiveTree = ConfigLoader.getV4SkillTree(passiveId);
      if (passiveTree !== treeName) continue;

      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect || typeof node.effect[key] !== 'number') continue;

      const val = node.effect[key] as number;
      found = true;

      switch (aggregation) {
        case 'sum': result += val; break;
        case 'product': result *= val; break;
        case 'max': result = Math.max(result, val); break;
        case 'min': result = Math.min(result, val); break;
      }
    }

    if (!found && aggregation === 'min') result = 0;
    this.genericCache.set(cacheKey, result);
    return result;
  }

  /** Aggregate a key from all unlocked passives across every tree. */
  getGlobalPassiveEffect(
    key: string,
    aggregation: 'sum' | 'product' | 'max' | 'min',
  ): number {
    const cacheKey = `global:${key}:${aggregation}`;
    const cached = this.genericCache.get(cacheKey);
    if (cached !== undefined) return cached as number;

    let result = aggregation === 'product' ? 1.0 : (aggregation === 'min' ? Infinity : 0);
    let found = false;

    for (const passiveId of this.progression.getUnlockedPassives()) {
      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect || typeof node.effect[key] !== 'number') continue;

      const val = node.effect[key] as number;
      found = true;

      switch (aggregation) {
        case 'sum': result += val; break;
        case 'product': result *= val; break;
        case 'max': result = Math.max(result, val); break;
        case 'min': result = Math.min(result, val); break;
      }
    }

    if (!found && aggregation === 'min') result = 0;
    this.genericCache.set(cacheKey, result);
    return result;
  }

  /** Resolve and cache all upgrade effects for a skill. */
  private resolve(skillId: string): ResolvedEffects {
    const cached = this.cache.get(skillId);
    if (cached) return cached;

    const upgrades = this.progression.getActiveUpgrades(skillId);

    let damageMultiplier = 1.0;
    let recoveryMsDelta = 0;
    let recoveryReduction = 1.0;
    let staggerChance = 0;
    let staggerDurationMs = 0;
    let movementSpeedMultiplier: number | undefined;
    let proximityReduction: number | undefined;

    for (const node of upgrades) {
      const effect = node.effect;
      if (!effect) continue;

      if (typeof effect['damage'] === 'number') {
        damageMultiplier *= effect['damage'] as number;
      }
      if (typeof effect['recoveryMs'] === 'number') {
        recoveryMsDelta += effect['recoveryMs'] as number;
      }
      if (typeof effect['staggerChance'] === 'number') {
        staggerChance = Math.max(staggerChance, effect['staggerChance'] as number);
      }
      if (typeof effect['staggerDurationMs'] === 'number') {
        staggerDurationMs = Math.max(staggerDurationMs, effect['staggerDurationMs'] as number);
      }
      if (typeof effect['movementSpeedMultiplier'] === 'number') {
        movementSpeedMultiplier = effect['movementSpeedMultiplier'] as number;
      }
      if (typeof effect['proximityReduction'] === 'number') {
        proximityReduction = effect['proximityReduction'] as number;
      }
    }

    // Check passives in the same tree for recovery reduction (e.g. Callused)
    const treeName = ConfigLoader.getV4SkillTree(skillId);
    if (treeName) {
      const passives = this.progression.getUnlockedPassives();
      for (const passiveId of passives) {
        const passiveTree = ConfigLoader.getV4SkillTree(passiveId);
        if (passiveTree !== treeName) continue;

        const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
        if (!node?.effect) continue;

        // recoveryReduction applies to a specific skill via appliesTo
        if (typeof node.effect['recoveryReduction'] === 'number' &&
            typeof node.effect['appliesTo'] === 'string') {
          if (node.effect['appliesTo'] === skillId) {
            recoveryReduction *= node.effect['recoveryReduction'] as number;
          }
        }
      }
    }

    const result: ResolvedEffects = {
      damageMultiplier, recoveryMsDelta, recoveryReduction,
      staggerChance, staggerDurationMs,
      movementSpeedMultiplier, proximityReduction,
    };
    this.cache.set(skillId, result);
    return result;
  }

  /** Get the resolved damage multiplier from upgrades (e.g. Impact Knuckles 1.3x). */
  getResolvedDamageMultiplier(skillId: string): number {
    return this.resolve(skillId).damageMultiplier;
  }

  /**
   * Get the resolved recovery duration in ms.
   * Applies additive deltas first (e.g. -100ms from Impact Knuckles),
   * then multiplicative reductions (e.g. 0.5x from Callused).
   */
  getResolvedRecoveryMs(skillId: string, baseRecoveryMs: number): number {
    const effects = this.resolve(skillId);
    const afterDelta = Math.max(0, baseRecoveryMs + effects.recoveryMsDelta);
    return Math.max(0, afterDelta * effects.recoveryReduction);
  }

  /** Get probabilistic stagger from upgrades (e.g. Rattled). Returns null if no stagger upgrade. */
  getStaggerEffect(skillId: string): StaggerEffect | null {
    const effects = this.resolve(skillId);
    if (effects.staggerChance <= 0) return null;
    return { chance: effects.staggerChance, durationMs: effects.staggerDurationMs };
  }

  /**
   * Check all unlocked passives across selected trees for damageReductionWhileAttacking.
   * Returns 0 if none found.
   */
  getDamageReductionWhileAttacking(): number {
    let totalDR = 0;
    const passives = this.progression.getUnlockedPassives();
    for (const passiveId of passives) {
      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect) continue;
      if (typeof node.effect['damageReductionWhileAttacking'] === 'number') {
        totalDR += node.effect['damageReductionWhileAttacking'] as number;
      }
    }
    return totalDR;
  }

  /** Aggregate maxHpMultiplier from all unlocked passives (e.g. Thick Skin 2.5x). */
  getMaxHpMultiplier(): number {
    let mult = 1.0;
    const passives = this.progression.getUnlockedPassives();
    for (const passiveId of passives) {
      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect) continue;
      if (typeof node.effect['maxHpMultiplier'] === 'number') {
        mult *= node.effect['maxHpMultiplier'] as number;
      }
    }
    return mult;
  }

  /** Aggregate stealthBackstabAngleBonus from unlocked passives (e.g. Blindside +40°). */
  getStealthBackstabAngleBonus(): number {
    let bonus = 0;
    const passives = this.progression.getUnlockedPassives();
    for (const passiveId of passives) {
      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect) continue;
      if (typeof node.effect['stealthBackstabAngleBonus'] === 'number') {
        bonus += node.effect['stealthBackstabAngleBonus'] as number;
      }
    }
    return bonus;
  }

  /** Get stealthBackstabMultiplier from unlocked passives (e.g. Blindside 2.0x). Returns null if none. */
  getStealthBackstabMultiplier(): number | null {
    let mult: number | null = null;
    const passives = this.progression.getUnlockedPassives();
    for (const passiveId of passives) {
      const node = ConfigLoader.getV4Node(passiveId) as V4NodeDefinition | undefined;
      if (!node?.effect) continue;
      if (typeof node.effect['stealthBackstabMultiplier'] === 'number') {
        mult = Math.max(mult ?? 0, node.effect['stealthBackstabMultiplier'] as number);
      }
    }
    return mult;
  }

  /** Get stealth movement speed multiplier from upgrades (e.g. Glide 0.55x). */
  getMovementSpeedMultiplier(skillId: string, baseMultiplier: number): number {
    const effects = this.resolve(skillId);
    return effects.movementSpeedMultiplier ?? baseMultiplier;
  }

  /** Get proximity reduction multiplier from upgrades (e.g. Low Profile 0.5x). */
  getProximityMultiplier(skillId: string): number {
    const effects = this.resolve(skillId);
    return effects.proximityReduction ?? 1.0;
  }
}
